import { Card, Tag, ProgressBar, Icon, Title, Text } from '@nimbus-ds/components'
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowupIcon,
  ArrowDownIcon,
  ArrowsHorizontalIcon,
} from '@nimbus-ds/icons'
import type { ExperimentResults, Experiment, RiskLevel } from '@/types'
import { RISK_VERDICT_ANNOTATION } from '@/components/experiments/RiskLevelSelector'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface DecisionHelperProps {
  experiment: Experiment | null
  results: ExperimentResults | null
}

type DecisionStatus = 'ready_winner' | 'ready_no_difference' | 'need_more_data' | 'inconclusive'

interface Decision {
  status: DecisionStatus
  title: string
  description: string
  recommendation: string
  confidence: number
  minSampleReached: boolean
  statisticallySignificant: boolean
}

const MIN_SAMPLE_SIZE = 100 // Minimum sessions per variant

export function DecisionHelper({ experiment, results }: DecisionHelperProps) {
  // In a "negative" experiment the variants are semantically swapped: control is
  // the new condition under test and treatment is the safe baseline. We frame the
  // verdict from the hypothesis's perspective, so a positive `hypothesisLift`
  // always means "the change you tested is winning".
  const isNegative = experiment?.is_negative === true
  const hypothesisLabel = isNegative ? 'Control' : 'Tratamiento'
  const baselineLabel = isNegative ? 'Tratamiento' : 'Control'

  const getDecision = (): Decision => {
    if (!results || !results.sessions) {
      return {
        status: 'need_more_data',
        title: 'Esperando Datos',
        description: 'El experimento aún no tiene suficientes datos para analizar.',
        recommendation: 'Espera a que se acumulen más sesiones antes de tomar decisiones.',
        confidence: 0,
        minSampleReached: false,
        statisticallySignificant: false,
      }
    }

    const controlSessions = results.sessions.control || 0
    const treatmentSessions = results.sessions.treatment || 0
    const totalSessions = controlSessions + treatmentSessions
    const confidence = results.confidence * 100
    const rawLift = results.lift * 100
    // For negative experiments lift must be computed relative to the actual
    // baseline (treatment). Sign-flipping the lambda value is wrong because the
    // denominator changes: e.g. -30.5% raw lift = +43.9% hypothesis-vs-baseline.
    let hypothesisLift = rawLift
    if (isNegative) {
      const ctrlRate = results.conversion_rate?.control ?? 0
      const trtRate = results.conversion_rate?.treatment ?? 0
      hypothesisLift = trtRate > 0 ? ((ctrlRate - trtRate) / trtRate) * 100 : -rawLift
    }

    const minSampleReached = controlSessions >= MIN_SAMPLE_SIZE && treatmentSessions >= MIN_SAMPLE_SIZE
    // Headline now rests on the BB+ROPE verdict, not the z-test. `bb_verdict` is control-vs-treatment:
    // 'treatment_better' = the treatment arm's CR is confidently higher; 'control_better' = control's is;
    // 'inconclusive' = neither past the ROPE (the small-N ⏳ OR a genuine within-margin tie).
    const bbVerdict = results.bb_verdict ?? 'inconclusive'
    const bbProb = Math.max(results.bb_p_better ?? 0, results.bb_p_worse ?? 0) * 100
    // Confidence number tracks the operative posterior tail when present (falls back to z confidence).
    const headlineConfidence = results.bb_verdict ? bbProb : confidence
    const hasConfidentWinner = bbVerdict === 'treatment_better' || bbVerdict === 'control_better'

    // LIFECYCLE GATE (evaluated FIRST, before any statistical claim). Below it the experiment is too
    // young to put a confident badge in front of a consultant — this is a maturity question, NOT an
    // inference one, so the copy is "collecting data", never "inconclusive" (which would read as a
    // statistical "we looked and it doesn't separate" and might make someone kill a young test).
    if (!minSampleReached) {
      const sessionsNeeded = Math.max(0, MIN_SAMPLE_SIZE - Math.min(controlSessions, treatmentSessions))
      return {
        status: 'need_more_data',
        title: 'Recolectando Datos',
        description: `Aún no hay suficientes datos para una conclusión confiable. Sesiones: Control ${controlSessions}, Tratamiento ${treatmentSessions} (mínimo ${MIN_SAMPLE_SIZE} por variante).`,
        recommendation: `Continúa el experimento. Faltan aproximadamente ${sessionsNeeded} sesiones en la variante más pequeña antes de poder concluir.`,
        confidence: headlineConfidence,
        minSampleReached: false,
        statisticallySignificant: false,
      }
    }

    // Above the gate: the BB+ROPE verdict is the inference claim.
    if (hasConfidentWinner) {
      // The winning ARM is treatment (treatment_better) or control (control_better). Map to the
      // hypothesis/baseline framing: in a negative experiment control IS the hypothesis under test.
      const treatmentWon = bbVerdict === 'treatment_better'
      const hypothesisWon = isNegative ? !treatmentWon : treatmentWon
      if (hypothesisWon) {
        return {
          status: 'ready_winner',
          title: `${hypothesisLabel} Gana`,
          description: isNegative
            ? `La hipótesis se confirma: ${hypothesisLabel.toLowerCase()} supera a ${baselineLabel.toLowerCase()} con +${hypothesisLift.toFixed(1)}% de lift (${headlineConfidence.toFixed(0)}% de probabilidad).`
            : `El tratamiento supera al control con un lift de +${hypothesisLift.toFixed(1)}% (${headlineConfidence.toFixed(0)}% de probabilidad de ser mejor).`,
          recommendation: isNegative
            ? 'El cambio testeado funciona: implementalo como la versión principal.'
            : 'Puedes implementar el tratamiento como la versión principal.',
          confidence: headlineConfidence,
          minSampleReached: true,
          statisticallySignificant: true,
        }
      } else {
        return {
          status: 'ready_winner',
          title: `${baselineLabel} Gana`,
          description: isNegative
            ? `La hipótesis se refuta: ${baselineLabel.toLowerCase()} supera al cambio testeado por ${Math.abs(hypothesisLift).toFixed(1)}% (${headlineConfidence.toFixed(0)}% de probabilidad).`
            : `El control supera al tratamiento (${headlineConfidence.toFixed(0)}% de probabilidad de que el tratamiento sea peor).`,
          recommendation: isNegative
            ? 'Mantené la versión actual: el cambio testeado empeora los resultados.'
            : 'Mantén la versión de control. El tratamiento tiene un impacto negativo.',
          confidence: headlineConfidence,
          minSampleReached: true,
          statisticallySignificant: true,
        }
      }
    }

    // Above the maturity gate, but the posterior does NOT confidently separate the arms past the ROPE:
    // either a genuine within-margin tie or not-yet-enough separation. This is a real STATISTICAL claim
    // (we have enough data and it doesn't clear the band), distinct from the lifecycle "collecting" state.
    return {
      status: 'inconclusive',
      title: 'Sin Diferencia Concluyente',
      description: `Con ${totalSessions.toLocaleString('es-ES')} sesiones, ninguna variante supera a la otra más allá del margen que definiste.`,
      recommendation: Math.abs(hypothesisLift) > 0.5
        ? `Hay una tendencia (${hypothesisLift > 0 ? '+' : ''}${hypothesisLift.toFixed(1)}% a favor de ${hypothesisLift > 0 ? hypothesisLabel.toLowerCase() : baselineLabel.toLowerCase()}), pero no es concluyente: seguí corriendo o asumí que no hay diferencia material.`
        : 'Las variantes rinden prácticamente igual. Elegí la que prefieras por otros criterios (simplicidad, costo).',
      confidence: headlineConfidence,
      minSampleReached: true,
      statisticallySignificant: false,
    }
  }

  const decision = getDecision()

  const getStatusIcon = () => {
    switch (decision.status) {
      case 'ready_winner': {
        // Hypothesis won: green up arrow. Hypothesis lost: amber down arrow.
        // For negative experiments this flips with the variant labels — the
        // winning side is whichever matches the hypothesis variant.
        const hypothesisWon = decision.title.startsWith(hypothesisLabel)
        return hypothesisWon
          ? <span className="text-green-600"><Icon source={<ArrowupIcon />} color="currentColor" /></span>
          : <span className="text-amber-600"><Icon source={<ArrowDownIcon />} color="currentColor" /></span>
      }
      case 'ready_no_difference':
        return <span className="text-blue-600"><Icon source={<ArrowsHorizontalIcon />} color="currentColor" /></span>
      case 'need_more_data':
        return <span className="text-gray-500"><Icon source={<ClockIcon />} color="currentColor" /></span>
      case 'inconclusive':
        return <span className="text-amber-500"><Icon source={<ExclamationTriangleIcon />} color="currentColor" /></span>
    }
  }

  const getStatusBadge = () => {
    switch (decision.status) {
      case 'ready_winner':
        return <Tag appearance="success">Decisión Lista</Tag>
      case 'ready_no_difference':
        return <Tag appearance="primary">Sin Diferencia</Tag>
      case 'need_more_data':
        return <Tag appearance="neutral">Recolectando Datos</Tag>
      case 'inconclusive':
        return <Tag appearance="warning">No Concluyente</Tag>
    }
  }

  // Decision posture that called the verdict (RISK-LEVELS-COPY.md): the results carry the level the
  // engine actually used (legacy experiments report 'safe'); fall back to the experiment record.
  const riskLevel: RiskLevel = results?.risk_level ?? experiment?.risk_level ?? 'safe'
  // The confidence bar tracks the posture (0.95 / 0.85 / 0.70), not a fixed 95%.
  const confidenceTarget = Math.round((results?.bb_threshold ?? 0.95) * 100)

  const controlSessions = results?.sessions?.control || 0
  const treatmentSessions = results?.sessions?.treatment || 0
  const sessionProgress = experiment?.session_limit
    ? ((experiment.current_sessions || 0) / experiment.session_limit) * 100
    : null

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <div>
              <Title as="h3">{decision.title}</Title>
              <Text className="mt-1">{decision.description}</Text>
            </div>
          </div>
          <div className="shrink-0 whitespace-nowrap flex flex-col items-end gap-1">
            {getStatusBadge()}
            {/* Posture annotation: a consultant reading "Tratamiento Gana" sees whether it was a
                fast lean (Ágil) or a firm call — only meaningful once a verdict was actually called. */}
            {decision.status === 'ready_winner' && (
              <Tag appearance="neutral">{RISK_VERDICT_ANNOTATION[riskLevel]}</Tag>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          {/* Recommendation */}
          <div className="p-4 rounded-lg bg-muted/50 border">
            <div className="flex items-start gap-2">
              <span className="text-primary mt-0.5 flex-shrink-0">
                <Icon source={<CheckCircleIcon />} color="currentColor" />
              </span>
              <div>
                <Text fontWeight="medium" color="neutral-textHigh">Recomendación</Text>
                <Text className="mt-1">{decision.recommendation}</Text>
              </div>
            </div>
          </div>

          {/* Progress indicators */}
          <div className="grid grid-cols-2 gap-4">
            {/* Sample Size Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <Text as="span">Muestra Mínima</Text>
                <span className="font-medium">
                  {decision.minSampleReached ? (
                    <span className="text-green-600">Alcanzada</span>
                  ) : (
                    <span className="text-amber-600">En progreso</span>
                  )}
                </span>
              </div>
              <div className="flex gap-2">
                <Text as="span" fontSize="caption">Control: {controlSessions}/{MIN_SAMPLE_SIZE}</Text>
                <Text as="span" fontSize="caption">|</Text>
                <Text as="span" fontSize="caption">Tratamiento: {treatmentSessions}/{MIN_SAMPLE_SIZE}</Text>
              </div>
            </div>

            {/* Confidence Progress. The headline uses the BB+ROPE posterior when
                available (§8), falls back to the z-test confidence otherwise.
                Both engines exposed side-by-side in the tooltip so consultants
                can answer "why this number?" without leaving the page. */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <TooltipProvider>
                  <Tooltip delayDuration={150}>
                    <TooltipTrigger asChild>
                      <span className="cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
                        Confianza
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm space-y-2">
                      <p className="text-[11px] text-muted-foreground">
                        Probabilidad de que la variante ganadora realmente supere a la otra
                        por un margen relevante. Ante la misma evidencia este número puede ser
                        levemente más conservador que el test tradicional.
                      </p>
                      <div className="space-y-1 border-t pt-2 text-[11px]">
                        <p className="font-medium">Modelo bayesiano (BB+ROPE)</p>
                        <div className="flex justify-between gap-3 text-muted-foreground">
                          <span>Probabilidad de mejora real</span>
                          <span className="font-semibold tabular-nums text-foreground">
                            {(Math.max(results?.bb_p_better ?? 0, results?.bb_p_worse ?? 0) * 100).toFixed(1)}%
                          </span>
                        </div>
                        {results?.bb_rope_width != null && (
                          <div className="flex justify-between gap-3 text-muted-foreground">
                            <span>Umbral (ROPE)</span>
                            <span className="tabular-nums text-foreground">
                              ±{((results.bb_rope_width ?? 0) * 100).toFixed(1)}%
                            </span>
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground/80">El motor que decide el veredicto.</p>
                      </div>
                      <div className="space-y-1 border-t pt-2 text-[11px]">
                        <p className="font-medium">Test frecuentista (referencia)</p>
                        <div className="flex justify-between gap-3 text-muted-foreground">
                          <span>Confianza (1 − p)</span>
                          <span className="tabular-nums text-foreground">
                            {(((results?.confidence as number) ?? 0) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex justify-between gap-3 text-muted-foreground">
                          <span>P-valor</span>
                          <span className="tabular-nums text-foreground">
                            {(results?.p_value ?? 0).toFixed(4)}
                          </span>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className={`font-medium ${decision.statisticallySignificant ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {decision.confidence.toFixed(1)}%
                </span>
              </div>
              <ProgressBar value={Math.min(decision.confidence, 100)} appearance="primary" />
              <Text fontSize="caption">
                Objetivo: {confidenceTarget}%
              </Text>
            </div>
          </div>

          {/* Session limit progress if configured */}
          {sessionProgress !== null && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex justify-between text-sm">
                <Text as="span">Límite de Sesiones</Text>
                <Text as="span" fontWeight="medium" color="neutral-textHigh">
                  {(experiment?.current_sessions || 0).toLocaleString()} / {experiment?.session_limit?.toLocaleString()}
                </Text>
              </div>
              <ProgressBar value={sessionProgress} appearance="primary" />
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}
